// ReportTheApp.js
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { handleReportSubmit } from "../reportUtils";
import { useTranslation } from "react-i18next";
import translations from "../../utils/language/translations.json";

const ReportTheApp = ({ userId, liveCsrfToken }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const t = (key) => translations.ReportModal[key][currentLang];

  // ----- state -----
  const [useAccountEmail, setUseAccountEmail] = useState(true);
  const [accountEmail, setAccountEmail] = useState("");
  const [customEmail, setCustomEmail] = useState("");
  const [screenshot, setScreenshot] = useState(null);
   const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    selectedPage: "",
    descriptionOptions: "",
    additionalComments: "",
  });

  // ----- constants/options -----
  const pageKeys = ["My Collection", "MarketPlace", "Add Card", "Profil", "Search"];
  const optionsByPage = {
    "My Collection": ["Filters", "EditCard", "GroupOption", "Visual-Issue", "Collection", "Not Sure"],
    "MarketPlace": ["Filters", "GroupOption", "Visual-Issue", "Not Sure"],
    "Add Card": ["Form", "Visual-Issue", "WrongOrMissingData", "Attributes-Issue", "ImageIsNotAMatch", "Not Sure", "Submission(Create Card)"],
    "Profil": ["ProfilDetails", "Password", "My Link", "ShippingTemplate", "TradingTemplate", "Visual-Issue", "Not Sure"],
    "Search": ["SimilarResultIssues", "Filters", "Visual-Issue", "Not Sure"],
  };

  // to keep react-select labels localized every render
  const pageOptions = useMemo(
    () => pageKeys.map((k) => ({ value: k, label: t(k) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentLang]
  );

  const descriptionOptions = useMemo(() => {
    const list = optionsByPage[formData.selectedPage] || [];
    return list.map((k) => ({ value: k, label: t(k) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.selectedPage, currentLang]);

  // ----- fetch account email -----
useEffect(() => {
  if (!userId) return;
  (async () => {
    try {
      const url = `${process.env.REACT_APP_API_BASE}/api/profil/getEmail/${encodeURIComponent(userId)}`;
      const res = await fetch(url, { method: "GET", credentials: "include" });

      if (!res.ok) {
        // Handle 404/500 gracefully
        console.warn('Email fetch failed:', res.status, res.statusText);
        setAccountEmail("");
        return;
      }

      const ct = res.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        // Avoid json() on empty/HTML responses
        const text = await res.text();
        console.warn('Expected JSON, got:', text);
        setAccountEmail("");
        return;
      }

      const json = await res.json();
      setAccountEmail(json?.Email || "");
      console.log('apres le get', json);
    } catch (e) {
      console.error("Error fetching user email:", e);
      setAccountEmail("");
    }
  })();
}, [userId]);


  // ----- handlers -----
  const onChangePage = (opt) => {
    setFormData((prev) => ({
      ...prev,
      selectedPage: opt?.value || "",
      descriptionOptions: "", // reset desc when page changes
    }));
  };

  const onChangeDescription = (opt) => {
    setFormData((prev) => ({ ...prev, descriptionOptions: opt?.value || "" }));
  };

  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/gif"];
    if (!allowed.includes(file.type)) {
      alert(t("Only PNG, JPG, JPEG, and GIF files are allowed."));
      return;
    }
    setScreenshot(file);
  };

// is the form ready to submit?
const emailToUse = useAccountEmail ? accountEmail : customEmail;
const canSubmit =
  Boolean(formData.selectedPage) &&
  Boolean(formData.descriptionOptions) &&
  Boolean(emailToUse);

// submit
const handleFormSubmit = async () => {
  if (!canSubmit || submitting) return;
  setSubmitting(true);

  const payload = { ...formData, userEmail: emailToUse };

  try {
    await handleReportSubmit(userId, payload, liveCsrfToken, screenshot, t);
    setFormData({ selectedPage: "", descriptionOptions: "", additionalComments: "" });
    setScreenshot(null);
    if (!useAccountEmail) setCustomEmail("");
  } catch (error) {
    console.error("Error submitting report:", error);
  } finally {
    setSubmitting(false);
  }
};

  // ----- react-select dark theme styles -----
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      background: "rgba(255,255,255,.06)",
      borderColor: state.isFocused ? "#0071E7" : "rgba(255,255,255,.14)",
      boxShadow: "none",
      minHeight: 40,
      borderRadius: 12,
      color: "#fff",
      ":hover": { borderColor: state.isFocused ? "#0071E7" : "rgba(255,255,255,.22)" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 .75rem" }),
    singleValue: (base) => ({ ...base, color: "#fff" }),
    placeholder: (base) => ({ ...base, color: "rgba(255,255,255,.65)" }),
    input: (base) => ({ ...base, color: "#fff" }),
    dropdownIndicator: (base) => ({ ...base, color: "rgba(255,255,255,.7)" }),
    indicatorSeparator: (base) => ({ ...base, background: "rgba(255,255,255,.12)" }),
    menu: (base) => ({
      ...base,
      background: "#151a22",
      border: "1px solid rgba(255,255,255,.14)",
      borderRadius: 12,
      boxShadow: "0 12px 28px rgba(0,0,0,.45)",
      overflow: "hidden",
      zIndex: 50,
    }),
    menuList: (base) => ({ ...base, padding: 0, maxHeight: 280 }),
    option: (base, state) => ({
      ...base,
      background: state.isSelected
        ? "#0a64e8"
        : state.isFocused
        ? "rgba(10,100,232,.15)"
        : "transparent",
      color: "#fff",
      cursor: "pointer",
      padding: "10px 12px",
      ":active": { background: "#0a64e8" },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  // ----- render -----
  return (
    <div className="report-issue-container">
      <h2 className="report-title">{t("Report Issue")}</h2>

      {/* Page */}
      <div className="form-group">
        <label>{t("Select Page")}</label>
        <Select
          styles={selectStyles}
          classNamePrefix="rs"
          menuPortalTarget={document.body}
          placeholder={t("Select Page")}
          value={pageOptions.find((o) => o.value === formData.selectedPage) || null}
          onChange={onChangePage}
          options={pageOptions}
        />
      </div>

      {/* Description */}
      <div className="form-group">
        <label>{t("Description")}</label>
        <Select
          styles={selectStyles}
          classNamePrefix="rs"
          menuPortalTarget={document.body}
          placeholder={t("Select Description")}
          value={
            descriptionOptions.find((o) => o.value === formData.descriptionOptions) || null
          }
          onChange={onChangeDescription}
          options={descriptionOptions}
          isDisabled={!formData.selectedPage}
        />
      </div>

      {/* Comments */}
      <div className="form-group">
        <label htmlFor="additionalComments">{t("Additional Comments")}</label>
        <textarea
          id="additionalComments"
          name="additionalComments"
          value={formData.additionalComments}
          onChange={handleTextChange}
          rows={5}
          className="custom-textarea"
        />
      </div>

      {/* Screenshot */}
      <div className="form-group">
        <label htmlFor="screenshot">{t("Upload Screenshot")}</label>
        <input
          type="file"
          id="screenshot"
          accept="image/png, image/jpeg, image/jpg, image/gif"
          onChange={handleFileChange}
          className="file-input"
        />
      </div>

      {/* Email */}
      <div className="form-group">
      <div className="email-checkbox-container">
        <div className="checkbox-container">
          
          <label htmlFor="useAccountEmail"><input
            id="useAccountEmail"
            type="checkbox"
            checked={useAccountEmail}
            style={{maxWidth:'fit-content',marginRight:'1rem'}}
            onChange={() => setUseAccountEmail((p) => !p)}
          />{t("Use my Account Email")} </label>
        </div>

        <input
          type="email"
          className="email-input"
          placeholder={t("Email")}
          value={useAccountEmail ? accountEmail : customEmail}
          disabled={useAccountEmail}
          onChange={(e) => setCustomEmail(e.target.value)}
        />
      </div>

 <button
  type="button"
  onClick={handleFormSubmit}
  className={`submit-btn ${submitting ? "is-loading" : ""}`}
  disabled={!canSubmit || submitting}
  aria-busy={submitting}
  aria-live="polite"
>
  {submitting ? (
    <>
      <span className="spinner" aria-hidden="true" />
      {t("Submit")}…
    </>
  ) : (
    t("Submit")
  )}
</button>
    </div>
    </div>
  );
};

export default ReportTheApp;
